/**
 * Harmony Button Directive (hxButton)
 *
 * Attribute directive following Angular Material pattern that applies to native <button> and <a> elements. Extends
 * HarmonyButtonBase for shared logic.
 *
 * Supports Moon Design System specifications:
 *
 * - 4 variants: Fill, Soft, Outline, Ghost
 * - 6 contexts: Brand, Neutral, Positive, Negative, Caution, Info
 * - 5 sizes: xs, sm, md, lg, xl
 *
 * @example
 *   ```html
 *   <button hxButton variant="fill" context="brand">Click Me</button>
 *   <button hxButton variant="outline" context="positive" size="lg">Save</button>
 *   ```;
 */
import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';
import { HxControlContext, HxControlSize, HxControlVariant } from '../../base/types';
import { HxIcon } from '../icon/icon';
import { HxButtonBase } from './button-base';

export type ButtonVariant = Extract<HxControlVariant, 'fill' | 'soft' | 'outline' | 'ghost'>;
export type ButtonContext = HxControlContext;
export type ButtonSize = Extract<HxControlSize, 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;
export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonAlign = 'start' | 'center' | 'end';

@Component({
  selector: 'button[hxButton], a[hxButton]',
  standalone: true,
  imports: [HxIcon],
  templateUrl: 'button.html',
  styleUrls: ['button.css'],
  host: {
    class: 'mdc-button',
    '[class]': 'buttonClasses()',
    '[attr.type]': '_getType()',
  },
  exportAs: 'hxButton',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxButton extends HxButtonBase {
  // Button-specific inputs
  readonly variant = input<ButtonVariant>('fill');
  readonly context = input<ButtonContext>('brand');
  readonly size = input<ButtonSize>('md');
  readonly startItem = input<string | null>(null);
  readonly endItem = input<string | null>(null);
  readonly fullWidth = input(false);
  readonly type = input<ButtonType>('button');
  readonly align = input<ButtonAlign>('center');

  // Computed signal for dynamic CSS classes (Tailwind via @apply + custom tokens)
  protected readonly buttonClasses = computed(() => ({
    // Base class (includes Tailwind utilities via @apply in CSS)
    'hx-button': true,

    // Variant + context combination classes
    [`hx-button-${this.variant()}`]: true,
    [`hx-button-${this.variant()}--${this.context()}`]: true,

    // Size class
    [`hx-button--${this.size()}`]: true,

    // State classes
    'hx-button--disabled': this.disabledState(),
    'hx-button--loading': this.loading(),

    // Alignment classes (need !important to override default justify-content: center)
    'hx-button--align-start': this.align() === 'start',
    'hx-button--align-center': this.align() === 'center',
    'hx-button--align-end': this.align() === 'end',

    // Layout utilities
    'hx-button--full-width': this.fullWidth(),
    'hx-button--with-icons': !!this.startItem() || !!this.endItem(),
  })); // Helper to only apply type attribute to <button> elements
  protected _getType(): string | null {
    if (this._elementRef.nativeElement.tagName === 'BUTTON') {
      return this.type();
    }
    return null;
  }
}
